

// ====== components/common/BarcodeGenerator.js ======
// Version avec vrais codes-barres lisibles par lecteur

import React, { useEffect, useRef } from 'react';

/**
 * Générateur de vrais codes-barres avec jsbarcode
 */
const BarcodeGenerator = ({ 
  value, 
  width = 160, 
  height = 40,
  className = '',
  style = {}
}) => {
  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!value) return;

    // Essayer d'importer jsbarcode dynamiquement
    const generateBarcode = async () => {
      try {
        // Import dynamique de jsbarcode
        const JsBarcode = (await import('jsbarcode')).default;
        
        if (svgRef.current) {
          // Générer le code-barres avec jsbarcode
          JsBarcode(svgRef.current, value, {
            format: 'CODE128', // Format standard lisible par tous les lecteurs
            width: 2,
            height: height - 15,
            displayValue: true,
            fontSize: 12,
            margin: 0,
            background: '#ffffff',
            lineColor: '#000000'
          });
        }
      } catch (error) {
        // Fallback vers la version simple si jsbarcode n'est pas installé
        generateFallbackBarcode();
      }
    };

    generateBarcode();
  }, [value, width, height]);

  // Version de secours si jsbarcode n'est pas disponible
  const generateFallbackBarcode = () => {
    if (!svgRef.current) return;
    
    // Pattern simple mais au moins cohérent
    const patterns = {
      '0': '1010001110',
      '1': '1110010010', 
      '2': '1011100010',
      '3': '1111001000',
      '4': '1000110010',
      '5': '1100010010',
      '6': '1001000110',
      '7': '1010000110',
      '8': '1100111010',
      '9': '1110111000'
    };

    let binaryString = '11010010000'; // Start pattern
    
    for (let char of value.toString()) {
      binaryString += patterns[char] || patterns['0'];
    }
    
    binaryString += '1100011101011'; // End pattern

    // Créer les barres SVG
    const svg = svgRef.current;
    svg.innerHTML = '';
    
    const barWidth = width / binaryString.length;
    
    binaryString.split('').forEach((bit, index) => {
      if (bit === '1') {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', index * barWidth);
        rect.setAttribute('y', 5);
        rect.setAttribute('width', barWidth);
        rect.setAttribute('height', height - 20);
        rect.setAttribute('fill', '#000000');
        svg.appendChild(rect);
      }
    });

    // Ajouter le texte
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', width / 2);
    text.setAttribute('y', height - 5);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('font-family', 'monospace');
    text.setAttribute('font-size', '10');
    text.setAttribute('fill', '#000000');
    text.textContent = value;
    svg.appendChild(text);
  };

  if (!value) {
    return (
      <div className={`text-center text-muted ${className}`} style={style}>
        <small>Aucun code-barres</small>
      </div>
    );
  }

  return (
    <div className={`barcode-container ${className}`} style={{ textAlign: 'center', ...style }}>
      <svg 
        ref={svgRef}
        width={width} 
        height={height} 
        style={{ 
          border: '1px solid #ddd',
          background: 'white',
          display: 'block',
          margin: '0 auto'
        }}
      >
        {/* Le contenu sera généré par jsbarcode ou le fallback */}
      </svg>
    </div>
  );
};

/**
 * Version simple pour les cas où on veut juste afficher le numéro
 */
export const BarcodeDisplay = ({ value, style = {} }) => {
  if (!value) return null;

  return (
    <div style={{
      border: '2px solid #000',
      padding: '4px 8px',
      background: 'white',
      fontFamily: 'monospace',
      fontWeight: 'bold',
      fontSize: '12px',
      letterSpacing: '2px',
      textAlign: 'center',
      margin: '4px auto',
      display: 'inline-block',
      ...style
    }}>
      {value}
    </div>
  );
};

/**
 * Composant de scan simplifié
 */
export const BarcodeScanner = ({ 
  onScan, 
  placeholder = "Scannez ou saisissez un code-barres"
}) => {
  const [barcode, setBarcode] = React.useState('');

  const handleScan = () => {
    if (barcode.trim()) {
      onScan?.(barcode.trim());
      setBarcode('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan();
    }
  };

  return (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={barcode}
        onChange={(e) => setBarcode(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
      />
      <button 
        className="btn btn-primary" 
        onClick={handleScan}
        disabled={!barcode.trim()}
      >
        Scanner
      </button>
    </div>
  );
};

export default BarcodeGenerator;